import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SearchX, X } from "lucide-react";
import { SectionHeading } from "../home/CtaButtons";
import { FILTER_GROUPS, groupOptions, matchesFilters, useDoorCatalog } from "../../lib/catalog";
import { DoorFilters } from "./DoorFilters";
import { DoorCard } from "./DoorCard";
import { DoorDetailDialog } from "./DoorDetailDialog";

const ActiveChips = ({ filters, labels, toggle }) => {
  const chips = Object.entries(filters).flatMap(([g, ids]) => ids.map((id) => [g, id]));
  if (!chips.length) return null;
  return (
    <ul className="flex flex-wrap gap-2" data-testid="door-active-filters">
      {chips.map(([g, id]) => (
        <li key={`${g}-${id}`}>
          <button type="button" onClick={() => toggle(g, id)} data-testid={`door-active-chip-${g}-${id}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-maas/30 bg-maas-soft px-3 py-1 text-[13px] font-medium text-maas-deep transition-colors hover:border-maas">
            {labels[`${g}:${id}`]} <X className="h-3.5 w-3.5" aria-hidden="true" /><span className="sr-only">filter verwijderen</span>
          </button>
        </li>
      ))}
    </ul>
  );
};

export const DoorGallery = ({ material = null }) => {
  const { doors, options, ready } = useDoorCatalog();
  const [params, setParams] = useSearchParams();
  const [filters, setFilters] = useState({});
  const base = useMemo(() => doors.filter((d) => !material || d.material === material), [doors, material]);
  const opts = useMemo(() => Object.fromEntries(options.map((o) => [o.id, o])), [options]);
  const groups = FILTER_GROUPS.filter((g) => !(material && g.key === "material"));
  const labels = useMemo(() => Object.fromEntries(FILTER_GROUPS.flatMap((g) => groupOptions(g.key, options).map((o) => [`${g.key}:${o.id}`, o.label]))), [options]);

  if (!ready || !base.length) return null;
  const shown = base.filter((d) => matchesFilters(d, filters));
  const open = base.find((d) => d.id === params.get("deur"));
  const toggle = (g, id) => setFilters((f) => {
    const cur = f[g] || [];
    return { ...f, [g]: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });
  const setDoor = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("deur", id); else next.delete("deur");
    setParams(next, { replace: true, preventScrollReset: true });
  };
  const anyFilter = Object.values(filters).some((v) => v.length);

  return (
    <section id="deurstijlen" aria-labelledby="deurstijlen-title" className="section scroll-mt-12 border-y border-slate-200 bg-slate-50" data-testid="door-gallery">
      <div className="container-tight">
        <SectionHeading id="deurstijlen-title" eyebrow="Deurenbrochure" title="Vind de deur die bij uw woning past"
          lead="Filter op stijl, type, kleur, glas en materiaal. Bekijk een deur in detail en vraag direct een vrijblijvende prijsindicatie aan." />
        <div className="mt-10 grid gap-8 lg:grid-cols-12">
          <DoorFilters doors={base} filters={filters} toggle={toggle} clear={() => setFilters({})} groups={groups} options={options} />
          <div className="lg:col-span-9">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[14.5px] text-slate-600" data-testid="door-result-count" aria-live="polite">
                <span className="font-semibold text-ink">{shown.length}</span> van {base.length} {base.length === 1 ? "deur" : "deuren"}
              </p>
              {anyFilter && <button type="button" onClick={() => setFilters({})} className="text-[14px] font-semibold text-maas hover:text-maas-hover" data-testid="door-clear-filters">Wis alle filters</button>}
            </div>
            <ActiveChips filters={filters} labels={labels} toggle={toggle} />
            {shown.length ? (
              <ul className="mt-5 grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="door-grid">
                {shown.map((d, i) => <DoorCard key={d.id} door={d} opts={opts} index={i} onOpen={() => setDoor(d.id)} />)}
              </ul>
            ) : (
              <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="door-empty">
                <SearchX className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
                <p className="mt-3 font-semibold text-ink">Geen deur gevonden met deze combinatie</p>
                <p className="mt-1 text-[14.5px] text-slate-500">Haal een filter weg of vraag ons advies via de prijsindicatie.</p>
              </div>
            )}
          </div>
        </div>
      </div>
      <DoorDetailDialog door={open} opts={opts} onClose={() => setDoor(null)} />
    </section>
  );
};
